// features/procurement/GRNListPage.tsx
/**
 * Goods Received Notes (GRN) Management — Executive UI/UX Redesign
 * Featuring origin PO tracking, one-click inventory confirmation, official PDF downloads,
 * and zero-silent-fail error handling.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card } from '@/components/ui/Card';
import {
    Truck, Plus, Search, X, RefreshCw,
    CheckCircle, Clock, Package, ArrowLeft,
    Building2, ChevronRight, Printer, RotateCcw,
    FileText, User, Store, ShieldCheck
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getGRNs,
    confirmGRN,
    downloadGrnPdf,
    getProcurementErrorMessage,
    GoodsReceivedNote,
} from './services/procurementApi';

// ============================================================
// STATUS PILL COMPONENT
// ============================================================

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    draft: { label: 'Draft (Pending Confirmation)', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    confirmed: { label: 'Confirmed (In Stock)', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    cancelled: { label: 'Cancelled', bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
};

function StatusPill({ status }: { status: string }) {
    const config = STATUS_CONFIG[status] || {
        label: status.replace(/_/g, ' '),
        bg: 'bg-slate-100',
        text: 'text-slate-700',
        dot: 'bg-slate-400',
    };

    return (
        <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold', config.bg, config.text)}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', config.dot)} />
            {config.label}
        </span>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function GRNListPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [confirmTarget, setConfirmTarget] = useState<GoodsReceivedNote | null>(null);

    // ============================================================
    // QUERY
    // ============================================================

    const { data: grns, isLoading, refetch } = useQuery<GoodsReceivedNote[]>({
        queryKey: ['grns-list', tenantId],
        queryFn: () => getGRNs(tenantId, { limit: 100 }),
        enabled: !!tenantId,
    });

    // ============================================================
    // MUTATION
    // ============================================================

    const confirmMutation = useMutation({
        mutationFn: (grnId: string) => confirmGRN(tenantId, grnId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['grns-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-grns'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-orders'] });
            queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
            setConfirmTarget(null);
            addToast({
                type: 'success',
                title: 'Goods Receipt Confirmed',
                message: 'Inventory has been credited and stock balance updated.',
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Confirmation Failed',
                message: getProcurementErrorMessage(err, 'Could not confirm goods received note.'),
            });
        },
    });

    // ============================================================
    // COMPUTED STATS & FILTERING
    // ============================================================

    const grnsList = grns || [];

    const filteredGrns = useMemo(() => {
        return grnsList.filter((g) => {
            const matchesSearch =
                !search ||
                g.grnNumber?.toLowerCase().includes(search.toLowerCase()) ||
                g.vendorName?.toLowerCase().includes(search.toLowerCase()) ||
                g.poNumber?.toLowerCase().includes(search.toLowerCase()) ||
                g.deliveryNoteNumber?.toLowerCase().includes(search.toLowerCase());

            const matchesStatus =
                statusFilter === 'all' || g.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [grnsList, search, statusFilter]);

    const pendingCount = grnsList.filter((g) => g.status === 'draft').length;
    const confirmedCount = grnsList.filter((g) => g.status === 'confirmed').length;
    const totalItems = grnsList.reduce((acc, g) => acc + (g.items?.length || 0), 0);

    const formatDate = (dateStr?: string) =>
        dateStr ? new Date(dateStr).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

    return (
        <div className="min-h-screen bg-slate-50/60 pb-16">
            {/* Header */}
            <div className="bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1680px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/procurement')}
                                className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition"
                                type="button"
                                title="Back to Procurement"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div className="p-3 bg-gradient-to-br from-cyan-500 to-sky-600 rounded-2xl shadow-lg shadow-cyan-500/20 text-white">
                                <Truck className="w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-black text-slate-900 tracking-tight">Goods Received Notes (GRN)</h1>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {grnsList.length} total receipts • {pendingCount} awaiting stock confirmation
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2.5">
                            <button
                                onClick={() => refetch()}
                                className="p-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
                                title="Refresh Goods Receipts"
                                type="button"
                            >
                                <RefreshCw size={15} />
                            </button>

                            <button
                                onClick={() => navigate('/procurement/grns/new')}
                                className="px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-sky-600 text-white rounded-xl hover:from-cyan-700 hover:to-sky-700 transition font-bold text-xs flex items-center gap-2 shadow-md shadow-cyan-500/20"
                                type="button"
                            >
                                <Plus size={16} />
                                <span>Receive Goods</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1680px] mx-auto px-6 lg:px-8 pt-6 space-y-6">

                {/* Metric Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Received Notes</span>
                        <p className="text-2xl font-black text-slate-900 mt-1">{grnsList.length}</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Awaiting Confirmation</span>
                        <p className={clsx('text-2xl font-black mt-1', pendingCount > 0 ? 'text-amber-600' : 'text-slate-900')}>
                            {pendingCount}
                        </p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Confirmed In Stock</span>
                        <p className="text-2xl font-black text-emerald-600 mt-1">{confirmedCount}</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Line Items</span>
                        <p className="text-2xl font-black text-cyan-700 mt-1">{totalItems}</p>
                    </div>
                </div>

                {/* Filter and Search Bar */}
                <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="relative w-full md:w-80">
                        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by GRN #, supplier, PO #..."
                            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition"
                        />
                    </div>

                    {/* Filter Pills */}
                    <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
                        {[
                            { id: 'all', label: 'All Receipts' },
                            { id: 'draft', label: 'Draft (Pending)' },
                            { id: 'confirmed', label: 'Confirmed (In Stock)' },
                        ].map((filter) => (
                            <button
                                key={filter.id}
                                onClick={() => setStatusFilter(filter.id)}
                                className={clsx(
                                    'px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all',
                                    statusFilter === filter.id
                                        ? 'bg-cyan-600 text-white shadow-sm'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                )}
                                type="button"
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table View */}
                {isLoading ? (
                    <div className="py-20 text-center flex flex-col items-center justify-center">
                        <Spinner size="lg" />
                        <p className="text-sm font-medium text-slate-500 mt-4">Loading goods received notes...</p>
                    </div>
                ) : filteredGrns.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center mx-auto mb-4">
                            <Truck size={28} />
                        </div>
                        <h3 className="text-base font-bold text-slate-900">No Goods Receipts Found</h3>
                        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                            {search ? `No receipts matching "${search}".` : 'Record and inspect delivery batches against supplier purchase orders.'}
                        </p>
                        <button
                            onClick={() => navigate('/procurement/grns/new')}
                            className="mt-5 px-5 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
                            type="button"
                        >
                            + Receive Goods
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/70">
                                        <th className="py-3.5 px-6">GRN Number</th>
                                        <th className="py-3.5 px-6">Supplier</th>
                                        <th className="py-3.5 px-6">Branch</th>
                                        <th className="py-3.5 px-6">Date Received</th>
                                        <th className="py-3.5 px-6">Status</th>
                                        <th className="py-3.5 px-6 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm">
                                    {filteredGrns.map((grn) => (
                                        <tr
                                            key={grn.id}
                                            onClick={() => navigate(`/procurement/grns/${grn.id}`)}
                                            className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                                        >
                                            {/* GRN & PO Number */}
                                            <td className="py-4 px-6 font-mono font-bold text-slate-900 flex items-center gap-2">
                                                <span>{grn.grnNumber}</span>
                                                {grn.poNumber && (
                                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-100 text-slate-600 border border-slate-200">
                                                        PO: {grn.poNumber}
                                                    </span>
                                                )}
                                            </td>

                                            {/* Supplier */}
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-7 h-7 rounded-lg bg-cyan-100 text-cyan-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                        {(grn.vendorName || 'S').slice(0, 1).toUpperCase()}
                                                    </div>
                                                    <span className="font-semibold text-slate-800">
                                                        {grn.vendorName || 'Supplier'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Branch */}
                                            <td className="py-4 px-6 text-xs text-slate-600">
                                                <div className="flex items-center gap-1.5">
                                                    <Building2 size={13} className="text-slate-400" />
                                                    <span>{grn.branchName || 'Main Branch'}</span>
                                                </div>
                                            </td>

                                            {/* Date Received */}
                                            <td className="py-4 px-6 text-xs text-slate-500">
                                                {formatDate(grn.createdAt)}
                                            </td>

                                            {/* Status Badge */}
                                            <td className="py-4 px-6">
                                                <StatusPill status={grn.status || 'draft'} />
                                            </td>

                                            {/* Actions */}
                                            <td className="py-4 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1.5">
                                                    {grn.status === 'draft' && (
                                                        <button
                                                            onClick={() => setConfirmTarget(grn)}
                                                            className="px-3 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-lg transition flex items-center gap-1 shadow-sm"
                                                            type="button"
                                                            title="Confirm Inventory Stock In"
                                                        >
                                                            <CheckCircle size={12} />
                                                            <span>Confirm</span>
                                                        </button>
                                                    )}

                                                    {grn.status === 'confirmed' && (
                                                        <button
                                                            onClick={() => navigate(`/procurement/returns/new?grnId=${grn.id}`)}
                                                            className="px-3 py-1 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 rounded-lg transition flex items-center gap-1 shadow-sm"
                                                            type="button"
                                                            title="Return Items from this GRN"
                                                        >
                                                            <RotateCcw size={12} />
                                                            <span>Return</span>
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={() => downloadGrnPdf(tenantId, grn.id, grn.grnNumber)}
                                                        className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition"
                                                        title="Export Official PDF"
                                                        type="button"
                                                    >
                                                        <Printer size={15} />
                                                    </button>

                                                    <button
                                                        onClick={() => navigate(`/procurement/grns/${grn.id}`)}
                                                        className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition"
                                                        type="button"
                                                    >
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* ============================================================ */}
            {/* CONFIRM GRN MODAL (INVITING EMERALD SUCCESS UI)              */}
            {/* ============================================================ */}
            <AnimatePresence>
                {confirmTarget && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setConfirmTarget(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center"
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', delay: 0.1 }}
                                className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4"
                            >
                                <CheckCircle size={26} />
                            </motion.div>
                            <h3 className="text-base font-bold text-slate-900">
                                Confirm Goods Receipt?
                            </h3>
                            <p className="text-xs text-slate-500 mt-2">
                                Confirming <strong>{confirmTarget.grnNumber}</strong> will immediately credit warehouse stock levels and close order fulfillment.
                            </p>

                            <div className="flex items-center gap-2.5 mt-6 pt-3 border-t border-slate-100">
                                <button
                                    onClick={() => setConfirmTarget(null)}
                                    className="flex-1 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => confirmMutation.mutate(confirmTarget.id)}
                                    disabled={confirmMutation.isPending}
                                    className="flex-1 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl text-xs font-bold hover:from-emerald-600 transition shadow-md shadow-emerald-500/20 flex items-center justify-center gap-2 disabled:opacity-50"
                                    type="button"
                                >
                                    {confirmMutation.isPending ? <Spinner size="xs" variant="white" /> : <CheckCircle size={14} />}
                                    <span>Confirm Stock</span>
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}